import { useState, useEffect, useRef } from 'react';
import api from '../../services/api';
import { toast } from 'sonner';
import Modal from '../../components/ui/Modal';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Plus, Edit2, Trash2, Upload, Star, Trophy } from 'lucide-react';
import { fmtUSD } from '../../lib/money';

const API_BASE = process.env.REACT_APP_BACKEND_URL || '';

export default function TiersPage() {
  const [tiers, setTiers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const logoInputRef = useRef(null);

  const defaultForm = { name: '', min_sc: 0, max_sc: 10000, min_referrals: 0, emoji: '', color: '#D4AF37', logo_url: '', perks: [], description: '', sort_order: 0, enabled: true };
  const [form, setForm] = useState(defaultForm);
  const [perksText, setPerksText] = useState('');

  useEffect(() => { fetchTiers(); }, []);

  const fetchTiers = async () => {
    try {
      const res = await api.get('/api/admin/tiers');
      setTiers(res.data.tiers || []);
    } catch { toast.error('Failed to load tiers'); }
    finally { setLoading(false); }
  };

  const handleLogoUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    try {
      const fd = new FormData();
      fd.append('file', file);
      fd.append('upload_type', 'payment_logo');
      const res = await api.post('/api/uploads', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      setForm(p => ({ ...p, logo_url: res.data.url || res.data.public_url }));
      toast.success('Logo uploaded');
    } catch { toast.error('Upload failed'); }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      const payload = { ...form, perks: perksText.split('\n').filter(Boolean).map(s => s.trim()) };
      if (editing) {
        await api.put(`/api/admin/tiers/${editing.id}`, payload);
        toast.success('Tier updated');
      } else {
        await api.post('/api/admin/tiers', payload);
        toast.success('Tier created');
      }
      setShowForm(false); setEditing(null); setForm(defaultForm); setPerksText(''); fetchTiers();
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed'); }
    finally { setSubmitting(false); }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this tier?')) return;
    try { await api.delete(`/api/admin/tiers/${id}`); toast.success('Deleted'); fetchTiers(); } catch { toast.error('Failed'); }
  };

  const openEdit = (t) => {
    setEditing(t);
    setForm({ name: t.name, min_sc: t.min_sc, max_sc: t.max_sc, min_referrals: t.min_referrals || 0, emoji: t.emoji || '', color: t.color || '#D4AF37', logo_url: t.logo_url || '', perks: t.perks || [], description: t.description || '', sort_order: t.sort_order || 0, enabled: t.enabled !== false });
    setPerksText((t.perks || []).join('\n'));
    setShowForm(true);
  };

  const resolveUrl = (url) => url?.startsWith('http') ? url : `${API_BASE}${url}`;

  if (loading) return <div className="flex items-center justify-center h-64"><div className="spinner" /></div>;

  return (
    <div className="space-y-5 animate-fade-in-up" data-testid="tiers-page">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-xl font-semibold flex items-center gap-2"><Trophy className="w-5 h-5 text-[#D4AF37]" /> VIP Tiers</h2>
          <p className="text-sm text-gray-400">Manage player tiers based on deposits + referrals</p>
        </div>
        <Button onClick={() => { setForm(defaultForm); setEditing(null); setPerksText(''); setShowForm(true); }} className="bg-[#D4AF37] text-black" data-testid="add-tier-btn">
          <Plus className="w-4 h-4 mr-2" /> Add Tier
        </Button>
      </div>

      <div className="space-y-2">
        {tiers.map(t => (
          <div key={t.id} className={`glass-card rounded-xl p-4 flex items-center gap-4 ${!t.enabled ? 'opacity-50' : ''}`} data-testid={`tier-${t.id}`}>
            <div className="w-12 h-12 rounded-xl flex items-center justify-center flex-shrink-0 text-2xl"
              style={{ background: `${t.color}20`, border: `1px solid ${t.color}40` }}>
              {t.logo_url ? <img src={resolveUrl(t.logo_url)} alt={t.name} className="w-full h-full rounded-xl object-cover" /> : t.emoji || '⭐'}
            </div>
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2">
                <h3 className="font-bold text-sm" style={{ color: t.color }}>{t.name}</h3>
                <span className="text-[9px] text-gray-500">{fmtUSD(t.min_sc)}+ deposits | {t.min_referrals || 0}+ refs</span>
                {!t.enabled && <span className="text-[9px] px-1.5 py-0.5 rounded-full bg-red-500/20 text-red-400">DISABLED</span>}
              </div>
              <div className="flex gap-1 mt-1">{(t.perks || []).map(p => <span key={p} className="text-[10px] bg-white/5 px-1.5 py-0.5 rounded">{p}</span>)}</div>
            </div>
            <div className="flex gap-1">
              <button onClick={() => openEdit(t)} className="p-2 rounded-lg hover:bg-white/5"><Edit2 className="w-4 h-4 text-gray-400" /></button>
              <button onClick={() => handleDelete(t.id)} className="p-2 rounded-lg hover:bg-white/5"><Trash2 className="w-4 h-4 text-red-400" /></button>
            </div>
          </div>
        ))}
      </div>

      <Modal open={showForm} onClose={() => setShowForm(false)} title={editing ? 'Edit Tier' : 'Create Tier'} maxWidth="max-w-md">
        <form onSubmit={handleSubmit} className="space-y-3">
          <Input value={form.name} onChange={e => setForm(p => ({ ...p, name: e.target.value }))} placeholder="Tier Name" className="bg-[#0B0E14] border-white/10" required />
          <div className="grid grid-cols-3 gap-3">
            <div><label className="text-xs text-gray-400 mb-1 block">Min Deposit ($)</label>
              <Input type="number" value={form.min_sc} onChange={e => setForm(p => ({ ...p, min_sc: parseFloat(e.target.value) || 0 }))} className="bg-[#0B0E14] border-white/10" /></div>
            <div><label className="text-xs text-gray-400 mb-1 block">Max Deposit ($)</label>
              <Input type="number" value={form.max_sc} onChange={e => setForm(p => ({ ...p, max_sc: parseFloat(e.target.value) || 0 }))} className="bg-[#0B0E14] border-white/10" /></div>
            <div><label className="text-xs text-gray-400 mb-1 block">Min Referrals</label>
              <Input type="number" value={form.min_referrals} onChange={e => setForm(p => ({ ...p, min_referrals: parseInt(e.target.value) || 0 }))} className="bg-[#0B0E14] border-white/10" /></div>
          </div>
          <div className="grid grid-cols-3 gap-3">
            <div><label className="text-xs text-gray-400 mb-1 block">Emoji</label>
              <Input value={form.emoji} onChange={e => setForm(p => ({ ...p, emoji: e.target.value }))} placeholder="🥉" className="bg-[#0B0E14] border-white/10" /></div>
            <div><label className="text-xs text-gray-400 mb-1 block">Color</label>
              <div className="flex gap-1"><input type="color" value={form.color} onChange={e => setForm(p => ({ ...p, color: e.target.value }))} className="w-10 h-10 rounded cursor-pointer" />
                <Input value={form.color} onChange={e => setForm(p => ({ ...p, color: e.target.value }))} className="bg-[#0B0E14] border-white/10 flex-1" /></div></div>
            <div><label className="text-xs text-gray-400 mb-1 block">Order</label>
              <Input type="number" value={form.sort_order} onChange={e => setForm(p => ({ ...p, sort_order: parseInt(e.target.value) || 0 }))} className="bg-[#0B0E14] border-white/10" /></div>
          </div>
          <div><label className="text-xs text-gray-400 mb-1 block">Logo (upload from device)</label>
            <input type="file" ref={logoInputRef} accept="image/*" className="hidden" onChange={handleLogoUpload} />
            <div className="flex items-center gap-3">
              {form.logo_url && <img src={resolveUrl(form.logo_url)} alt="Logo" className="w-10 h-10 rounded-lg object-cover" />}
              <Button type="button" onClick={() => logoInputRef.current?.click()} variant="outline" className="border-white/10 text-xs">
                <Upload className="w-3 h-3 mr-1" /> {form.logo_url ? 'Change' : 'Upload'} Logo
              </Button>
              {form.logo_url && <button type="button" onClick={() => setForm(p => ({ ...p, logo_url: '' }))} className="text-red-400 text-xs">Remove</button>}
            </div>
          </div>
          <div>
            <label className="text-xs text-gray-400 mb-1 block">Perks (one per line)</label>
            <textarea
              value={perksText}
              onChange={e => setPerksText(e.target.value)}
              rows={3}
              placeholder={"\ud83c\udf81 10% deposit bonus\n[support] Priority support\n[fast] Faster withdrawals"}
              className="w-full rounded-md bg-[#0B0E14] border border-white/10 text-white p-3 text-sm font-mono"
              data-testid="tier-perks-input"
            />
            <p className="text-[10px] text-gray-500 mt-1 leading-snug">
              Tip: prefix each line with an emoji (\ud83c\udf81) or a <span className="font-mono">[shortcode]</span> to render an icon. Available: <span className="font-mono">[gift] [sparkles] [star] [zap] [trophy] [crown] [gem] [rocket] [support] [shield] [verified] [coins] [dollar] [fast] [priority] [fire] [clock] [heart] [medal] [award]</span>.
            </p>
          </div>
          <div>
            <label className="text-xs text-gray-400 mb-1 block">Description (shown when player taps a tier card)</label>
            <textarea
              value={form.description}
              onChange={e => setForm(p => ({ ...p, description: e.target.value }))}
              rows={4}
              maxLength={600}
              placeholder={"Reach **Gold** tier to unlock priority support, faster withdrawals, and exclusive seasonal rewards.\n\nLearn more in our [tier guide](https://example.com/tiers)."}
              className="w-full rounded-md bg-[#0B0E14] border border-white/10 text-white p-3 text-sm"
              data-testid="tier-description-input"
            />
            <div className="flex items-center justify-between text-[10px] text-gray-500 mt-1">
              <span>
                Markdown: <span className="font-mono">**bold**</span>, <span className="font-mono">*italic*</span>, <span className="font-mono">[link](https://…)</span>, blank line = paragraph.
              </span>
              <span>{(form.description || '').length}/600</span>
            </div>
          </div>
          <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={form.enabled} onChange={e => setForm(p => ({ ...p, enabled: e.target.checked }))} className="accent-[#D4AF37]" /> Enabled</label>
          <Button type="submit" disabled={submitting} className="w-full bg-[#D4AF37] text-black">{submitting ? 'Saving...' : editing ? 'Update Tier' : 'Create Tier'}</Button>
        </form>
      </Modal>
    </div>
  );
}
